import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
`;

const BackButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const Form = styled.form`
  max-width: 600px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #cccccc;
  font-weight: 600;
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #444;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 1rem;
  
  &:focus {
    border-color: #cc0000;
    outline: none;
  }
`;

const AmountControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-top: 1rem;
`;

const AmountButton = styled.button`
  background: ${props => props.primary ? '#cc0000' : '#444'};
  color: white;
  border: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? '#aa0000' : '#555'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AmountDisplay = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  min-width: 100px;
  text-align: center;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: #cccccc;
  font-size: 1rem;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const SaveButton = styled.button`
  background: #cc0000;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 120px;
  
  &:hover {
    background: #aa0000;
  }
`;

const QRButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 120px;
  
  &:hover {
    background: #0052a3;
  }
`;

const PrintButton = styled.button`
  background: #ff6600;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 120px;
  
  &:hover {
    background: #e55a00;
  }
`;

const ConfirmButton = styled.button`
  background: #00aa00;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 120px;
  
  &:hover {
    background: #008800;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 120px;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const SongTitle = styled.h1`
  color: #ffffff;
  font-size: 2rem;
  margin-bottom: 2rem;
  font-weight: 700;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #cc0000;
`;

const FileUploadContainer = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border: 2px dashed #444;
  border-radius: 6px;
  text-align: center;
  background: rgba(255, 255, 255, 0.05);
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  background: #0066cc;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: #0052a3;
  }
`;

const PDFStatus = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(0, 170, 0, 0.1);
  border: 1px solid #00aa00;
  border-radius: 6px;
  color: #00aa00;
`;

const PDFActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const ViewPDFButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #0052a3;
  }
`;

const DeletePDFButton = styled.button`
  background: #cc0000;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #aa0000;
  }
`;

const UploadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #444;
  border-top: 2px solid #0066cc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function AdvancedSongPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [currentAmount, setCurrentAmount] = useState(0);
  const [targetAmount, setTargetAmount] = useState(0);
  const [status, setStatus] = useState('active');
  const [pdfFilename, setPdfFilename] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadSong();
  }, [id]);

  const loadSong = async () => {
    try {
      setLoading(true);
      const songData = await songAPI.getSong(id);
      setSong(songData);
      setName(songData.name);
      setCurrentAmount(songData.current_amount);
      setTargetAmount(songData.target_amount);
      setStatus(songData.status);
      setPdfFilename(songData.pdf_filename);
    } catch (error) {
      console.error('Error loading song:', error);
      toast.error('Failed to load song');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustCurrentAmount = (adjustment) => {
    const newAmount = Math.max(0, currentAmount + adjustment);
    setCurrentAmount(newAmount);
  };

  const handleAdjustTargetAmount = (adjustment) => {
    const newAmount = Math.max(0, targetAmount + adjustment);
    setTargetAmount(newAmount);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await songAPI.updateSong(id, {
        name: name.trim(),
        current_amount: currentAmount,
        target_amount: targetAmount,
        status
      });
      toast.success('Song updated successfully!');
      navigate('/advanced');
    } catch (error) {
      console.error('Error updating song:', error);
      toast.error(error.response?.data?.error || 'Failed to update song');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await songAPI.deleteSong(id);
      toast.success('Song deleted successfully!');
      navigate('/advanced');
    } catch (error) {
      console.error('Error deleting song:', error);
      toast.error(error.response?.data?.error || 'Failed to delete song');
    }
  };

  const handleGenerateQRPDF = async () => {
    try {
      const pdfBlob = await songAPI.generateQRPDF(id);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name.replace(/[^a-zA-Z0-9]/g, '_')}_QR.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('QR PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating QR PDF:', error);
      toast.error('Failed to generate QR PDF');
    }
  };

  const handleConfirmCount = async () => {
    try {
      await songAPI.confirmCount(id);
      toast.success('Count confirmed successfully!');
    } catch (error) {
      console.error('Error confirming count:', error);
      toast.error('Failed to confirm count');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    if (file.size > 17 * 1024 * 1024) { // 17MB limit
      toast.error(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the maximum allowed size of 17MB`);
      return;
    }

    try {
      setUploading(true);
      const updatedSong = await songAPI.uploadPDF(id, file);
      setPdfFilename(updatedSong.pdf_filename);
      toast.success('PDF uploaded successfully!');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to upload PDF. Please check the file and try again.';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleViewPDF = async () => {
    try {
      const pdfBlob = await songAPI.getPDF(id);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to view PDF. The file may be missing. Please try re-uploading it.';
      toast.error(errorMessage);
    }
  };

  const handleDeletePDF = async () => {
    if (!window.confirm('Are you sure you want to delete the uploaded PDF?')) {
      return;
    }

    try {
      await songAPI.deletePDF(id);
      setPdfFilename(null);
      toast.success('PDF deleted successfully!');
    } catch (error) {
      console.error('Error deleting PDF:', error);
      toast.error('Failed to delete PDF');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!song) {
    return (
      <PageContainer>
        <ErrorMessage>
          <h2>Song not found</h2>
          <p>The song you're looking for doesn't exist.</p>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/advanced')}>
        ← Back to Advanced Overview
      </BackButton>

      <SongTitle>Edit Song</SongTitle>

      <Form onSubmit={handleSave}>
        <FormGroup>
          <Label htmlFor="name">Song Name</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Current Amount</Label>
          <AmountControls>
            <AmountButton
              type="button"
              onClick={() => handleAdjustCurrentAmount(-1)}
            >
              −
            </AmountButton>
            <AmountDisplay>{currentAmount}</AmountDisplay>
            <AmountButton
              type="button"
              primary
              onClick={() => handleAdjustCurrentAmount(1)}
            >
              +
            </AmountButton>
          </AmountControls>
        </FormGroup>

        <FormGroup>
          <Label>Target Amount</Label>
          <AmountControls>
            <AmountButton
              type="button"
              onClick={() => handleAdjustTargetAmount(-1)}
            >
              −
            </AmountButton>
            <AmountDisplay>{targetAmount}</AmountDisplay>
            <AmountButton
              type="button"
              primary
              onClick={() => handleAdjustTargetAmount(1)}
            >
              +
            </AmountButton>
          </AmountControls>
        </FormGroup>

        <FormGroup>
          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="active"
              checked={status === 'active'}
              onChange={(e) => setStatus(e.target.checked ? 'active' : 'archived')}
            />
            <CheckboxLabel htmlFor="active">Active</CheckboxLabel>
          </CheckboxGroup>
        </FormGroup>

        <FormGroup>
          <Label>Sheet Music PDF</Label>
          <FileUploadContainer>
            {pdfFilename ? (
              <PDFStatus>
                <strong>✓ PDF Uploaded</strong>
                <PDFActions>
                  <ViewPDFButton onClick={handleViewPDF}>
                    📄 View PDF
                  </ViewPDFButton>
                  <DeletePDFButton onClick={handleDeletePDF}>
                    🗑️ Delete PDF
                  </DeletePDFButton>
                </PDFActions>
              </PDFStatus>
            ) : (
              <div>
                <p style={{ color: '#cccccc', marginBottom: '1rem' }}>
                  Upload a PDF file for this song's sheet music
                </p>
                <FileInput
                  type="file"
                  id="pdf-upload"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <FileInputLabel htmlFor="pdf-upload">
                  {uploading ? (
                    <>
                      <UploadingSpinner />
                      Uploading...
                    </>
                  ) : (
                    '📁 Choose PDF File'
                  )}
                </FileInputLabel>
                <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Max file size: 17MB
                </p>
              </div>
            )}
          </FileUploadContainer>
        </FormGroup>

        <ButtonGroup>
          <SaveButton type="submit">Save Changes</SaveButton>
          <ConfirmButton type="button" onClick={handleConfirmCount}>
            ✓ Confirm Count
          </ConfirmButton>
          <PrintButton type="button" onClick={() => navigate(`/print/${id}`)}>
            🖨️ Print Page
          </PrintButton>
          <QRButton type="button" onClick={handleGenerateQRPDF}>
            📄 Generate QR PDF
          </QRButton>
          <DeleteButton type="button" onClick={handleDelete}>
            Delete Song
          </DeleteButton>
        </ButtonGroup>
      </Form>
    </PageContainer>
  );
}

export default AdvancedSongPage;

